import type { ReactNode } from 'react';

import type {
  EmptyLayoutAsset,
  ImageLayoutAsset,
  PdfLayoutAsset,
  ViewerPosition,
} from '../contracts/models';
import {
  PdfPageViewport,
  type PdfPageViewportProps,
} from './PdfPageViewport';
import {
  SvgLayoutViewport,
  type SvgLayoutDimensions,
  type SvgLayoutViewportProps,
} from './SvgLayoutViewport';

type LayoutAssetOverlay =
  | ReactNode
  | ((dimensions: SvgLayoutDimensions) => ReactNode);

interface LayoutAssetViewportCommonProps {
  position: ViewerPosition;
  scale: number;
  children?: LayoutAssetOverlay;
}

export type ImageLayoutViewportOptions = Omit<
  SvgLayoutViewportProps,
  | 'position'
  | 'scale'
  | 'imageUrl'
  | 'flippedHorizontal'
  | 'flippedVertical'
  | 'children'
>;

export type PdfLayoutViewportOptions = Omit<
  PdfPageViewportProps,
  | 'fileUrl'
  | 'pageNumber'
  | 'rotation'
  | 'cropRegion'
  | 'rasterFallback'
  | 'flippedHorizontal'
  | 'flippedVertical'
  | 'position'
  | 'scale'
  | 'children'
>;

export interface ImageLayoutAssetViewportProps
  extends LayoutAssetViewportCommonProps {
  asset: ImageLayoutAsset;
  imageViewport: ImageLayoutViewportOptions;
}

export interface PdfLayoutAssetViewportProps
  extends LayoutAssetViewportCommonProps {
  asset: PdfLayoutAsset;
  pdfViewport: PdfLayoutViewportOptions;
}

export interface EmptyLayoutAssetViewportProps
  extends LayoutAssetViewportCommonProps {
  asset: EmptyLayoutAsset;
  emptyFallback?: ReactNode;
}

export type LayoutAssetViewportProps =
  | ImageLayoutAssetViewportProps
  | PdfLayoutAssetViewportProps
  | EmptyLayoutAssetViewportProps;

const isEmptyAssetViewport = (
  props: LayoutAssetViewportProps,
): props is EmptyLayoutAssetViewportProps => props.asset.kind === 'empty';

const isImageAssetViewport = (
  props: LayoutAssetViewportProps,
): props is ImageLayoutAssetViewportProps => props.asset.kind === 'image';

/**
 * Canonical layout-asset switch.
 *
 * The host owns interaction policy and supplies viewport callbacks. This
 * component keeps image/PDF asset selection and transform mapping identical
 * across host applications while exposing one overlay render contract.
 */
export const LayoutAssetViewport = (props: LayoutAssetViewportProps) => {
  const { position, scale, children } = props;

  if (isEmptyAssetViewport(props)) {
    return <>{props.emptyFallback ?? null}</>;
  }

  if (isImageAssetViewport(props)) {
    const { imageViewport } = props;
    const overlay =
      typeof children === 'function'
        ? children(imageViewport.dimensions)
        : children;
    return (
      <SvgLayoutViewport
        {...imageViewport}
        position={position}
        scale={scale}
        imageUrl={props.asset.url}
        flippedHorizontal={props.asset.flippedHorizontal}
        flippedVertical={props.asset.flippedVertical}
      >
        {overlay}
      </SvgLayoutViewport>
    );
  }

  return (
    <PdfPageViewport
      {...props.pdfViewport}
      position={position}
      scale={scale}
      fileUrl={props.asset.url}
      pageNumber={props.asset.pageNumber}
      rotation={props.asset.rotation}
      cropRegion={props.asset.cropRegion}
      rasterFallback={props.asset.rasterFallback}
      flippedHorizontal={props.asset.flippedHorizontal}
      flippedVertical={props.asset.flippedVertical}
    >
      {children}
    </PdfPageViewport>
  );
};
